import {
  ArrowRightOutlined,
  CalendarOutlined,
  CloseCircleFilled,
} from '@ant-design/icons'
import { Popover } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useState } from 'react'

import cn from './AtomDateRangePicker.module.scss'
import CalendarPanel from './CalendarPanel/CalendarPanel'
import { DATE_RANGE_FORMAT, type DateRangeValue } from './lib/dateRangeCalendar'

type DateRangePickerProps = {
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
  fromPlaceholder?: string
  toPlaceholder?: string
  isOrganisationSelected?: boolean
  allowFutureDates?: boolean
  className?: string
}

function AtomDateRangePicker({
  value,
  onChange,
  fromPlaceholder = 'Период с',
  toPlaceholder = 'по',
  isOrganisationSelected = false,
  allowFutureDates = false,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const maxDate = allowFutureDates ? null : dayjs().endOf('day')
  const hasValue = value.from !== null || value.to !== null
  const accentClassName = isOrganisationSelected && cn.accentSelected

  const panels = (
    <div className={classNames(cn.panels, accentClassName)}>
      <CalendarPanel
        title="Дата с"
        value={value.from}
        range={value}
        maxDate={maxDate}
        onSelect={(from) =>
          onChange({ ...value, from: from ? from.startOf('day') : null })
        }
      />
      <span className={cn.divider} />
      <CalendarPanel
        title="Дата по"
        value={value.to}
        range={value}
        maxDate={maxDate}
        onSelect={(to) =>
          onChange({ ...value, to: to ? to.endOf('day') : null })
        }
      />
    </div>
  )

  return (
    <Popover
      content={panels}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      arrow={false}
      rootClassName={classNames(cn.popover, accentClassName)}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen(true)
          }
        }}
        className={classNames(
          cn.trigger,
          accentClassName,
          open && cn.triggerOpen,
          className
        )}
      >
        <CalendarOutlined className={cn.icon} />
        <span className={classNames(cn.value, !value.from && cn.placeholder)}>
          {value.from ? value.from.format(DATE_RANGE_FORMAT) : fromPlaceholder}
        </span>
        <ArrowRightOutlined className={cn.arrow} />
        <span className={classNames(cn.value, !value.to && cn.placeholder)}>
          {value.to ? value.to.format(DATE_RANGE_FORMAT) : toPlaceholder}
        </span>
        {hasValue && (
          <button
            type="button"
            className={cn.clear}
            aria-label="Очистить период"
            onClick={(event) => {
              event.stopPropagation()
              onChange({ from: null, to: null })
            }}
          >
            <CloseCircleFilled />
          </button>
        )}
      </div>
    </Popover>
  )
}

export default AtomDateRangePicker
